import axios from 'axios';
import { baseURL } from '../Constants';

const url = `${baseURL}/PlayerScout`;

export interface ScoutPlayerResult {
    steamId: string;
    playerName: string | null;
    teamName: string | null;
    teamId: string | null;
    division: string | null;
    roleRounds: number;
    gamesInRole: number;
    rolePercentage: number;
    totalTimeSeconds: number;
    roundsInArea: number;
    areaTimePercentage: number;
    gamesCount: number;
    avgRating21: number;
    avgAdr: number;
    avgKd: number;
    avgKast: number;
}

export interface ScoutResponse {
    searchType: 'role' | 'area';
    roleCategory: string | null;
    mapName: string | null;
    areaName: string | null;
    side: string | null;
    season: number;
    division: string | null;
    minThreshold: number;
    players: ScoutPlayerResult[];
}

export interface ScoutMapArea {
    id: number;
    areaName: string;
}

export interface ScoutFilters {
    seasons: number[];
    maps: string[];
    roleCategories: string[];
}

export const scoutByRole = async (
    roleCategory: string,
    season: number,
    side?: string | null,
    division?: string | null,
    mapName?: string | null,
    minRounds: number = 10,
    limit: number = 50,
    minRolePercentage: number = 0
): Promise<ScoutResponse> => {
    const params = new URLSearchParams();
    params.append('roleCategory', roleCategory);
    params.append('season', season.toString());
    if (side) params.append('side', side);
    if (division) params.append('division', division);
    if (mapName) params.append('mapName', mapName);
    params.append('minRounds', minRounds.toString());
    params.append('limit', limit.toString());
    params.append('minRolePercentage', minRolePercentage.toString());
    const response = await axios.get(`${url}/role?${params.toString()}`);
    return response.data;
};

export const scoutByArea = async (
    areaId: number,
    season: number,
    side?: string | null,
    division?: string | null,
    minTimeSeconds: number = 60,
    limit: number = 50
): Promise<ScoutResponse> => {
    const params = new URLSearchParams();
    params.append('areaId', areaId.toString());
    params.append('season', season.toString());
    if (side) params.append('side', side);
    if (division) params.append('division', division);
    params.append('minTimeSeconds', minTimeSeconds.toString());
    params.append('limit', limit.toString());
    const response = await axios.get(`${url}/area?${params.toString()}`);
    return response.data;
};

export const getScoutFilters = async (): Promise<ScoutFilters> => {
    const response = await axios.get(`${url}/filters`);
    return response.data;
};

export const getScoutMaps = async (season: number): Promise<string[]> => {
    const response = await axios.get(`${url}/maps`, { params: { season } });
    return response.data;
};

export const getAreasByMap = async (mapName: string): Promise<ScoutMapArea[]> => {
    const response = await axios.get(`${url}/areas`, { params: { mapName } });
    return response.data;
};

// ---- "My demos" scout: pooled across the donator's own uploaded demos (JWT-scoped to the owner) ----

const userDemoUrl = `${baseURL}/UserDemo`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

export const scoutMyDemosRole = async (
    roleCategory: string,
    side?: string | null,
    mapName?: string | null,
    minRounds: number = 3,
    minRolePercentage: number = 0,
    limit: number = 50
): Promise<ScoutPlayerResult[]> => {
    const params = new URLSearchParams();
    params.append('roleCategory', roleCategory);
    if (side) params.append('side', side);
    if (mapName) params.append('mapName', mapName);
    params.append('minRounds', minRounds.toString());
    params.append('minRolePercentage', minRolePercentage.toString());
    params.append('limit', limit.toString());
    const response = await axios.get(`${userDemoUrl}/scout/role?${params.toString()}`, { headers: authHeader() });
    return response.data;
};

export const scoutMyDemosArea = async (
    areaId: number,
    side?: string | null,
    minTimeSeconds: number = 20,
    limit: number = 50
): Promise<ScoutPlayerResult[]> => {
    const params = new URLSearchParams();
    params.append('areaId', areaId.toString());
    if (side) params.append('side', side);
    params.append('minTimeSeconds', minTimeSeconds.toString());
    params.append('limit', limit.toString());
    const response = await axios.get(`${userDemoUrl}/scout/area?${params.toString()}`, { headers: authHeader() });
    return response.data;
};

export const getMyDemosScoutMaps = async (): Promise<string[]> => {
    const response = await axios.get(`${userDemoUrl}/scout/maps`, { headers: authHeader() });
    return response.data;
};

export const getMyDemosScoutAreas = async (mapName: string): Promise<ScoutMapArea[]> => {
    const response = await axios.get(`${userDemoUrl}/scout/areas`, { params: { mapName }, headers: authHeader() });
    return response.data;
};
